import { useEffect, useState, type RefObject } from 'react';

export interface ScrollToTopProps {
  /** The scroll container to watch/scroll. Defaults to the window. */
  scrollRef?: RefObject<HTMLElement | null>;
  /** Show the button after this many pixels of scroll. */
  threshold?: number;
}

/** A fixed bottom-right button that appears after scrolling and returns to the top. */
export function ScrollToTop({ scrollRef, threshold = 300 }: ScrollToTopProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target: HTMLElement | Window = scrollRef?.current ?? window;
    const getTop = () => (scrollRef?.current ? scrollRef.current.scrollTop : window.scrollY);
    const onScroll = () => setVisible(getTop() > threshold);
    target.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => target.removeEventListener('scroll', onScroll);
  }, [scrollRef, threshold]);

  if (!visible) return null;

  const toTop = () => {
    const el = scrollRef?.current;
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      className="btn btn-primary btn-icon rounded-pill shadow"
      aria-label="Back to top"
      onClick={toTop}
      style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1040, width: 44, height: 44 }}
    >
      <i className="ph-arrow-up" />
    </button>
  );
}
